import { useState } from "react";
import { getSessionToken, getTokenInfo } from "@/api/auth";
import { updateUserData } from "@/api/users";
import { checkPassword } from "./checkPassword";

/**
 * Change-password card in the Agents Profile design. Self-contained: it looks up the
 * signed-in user, saves only the password, then sends the person back to sign in
 * (the token must be refreshed after a password change — same as the old Profile page).
 */
export function PasswordPanel() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const input =
    "w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-gray-900 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20";

  const save = async () => {
    const problem = checkPassword(password, confirm);
    if (problem) return setMessage({ ok: false, text: problem });
    setSaving(true);
    setMessage(null);
    try {
      const token = getSessionToken();
      const me = await getTokenInfo(token);
      if (!me?.id) throw new Error("We couldn't find your account. Sign in again and retry.");
      const res = await updateUserData(me.id, { token, data: { password } });
      if (res?.error || res instanceof Error) throw new Error(res.error || "The password was not saved.");
      setMessage({ ok: true, text: "Password changed. Taking you to sign in again…" });
      setTimeout(() => (window.location.href = "/login"), 1200);
    } catch (e: any) {
      setMessage({ ok: false, text: e?.message || "The password was not saved." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="password-panel" className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-5">Password</h3>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">New password</label>
          <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-600">Confirm new password</label>
          <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={input} />
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <p className={`text-xs ${message ? (message.ok ? "text-green-600" : "text-red-600") : "text-gray-400"}`}>
          {message ? message.text : "You'll be asked to sign in again after changing it."}
        </p>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Change password"}
        </button>
      </div>
    </div>
  );
}
